import { cn } from "@/lib/utils";

export const PageHeader = ({ eyebrow, title, desc, children }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between border-b border-border pb-6 mb-6 rise">
    <div>
      {eyebrow && <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#007AFF] mb-2">{eyebrow}</div>}
      <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="page-title">{title}</h1>
      {desc && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{desc}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const TONES = {
  green: "text-[#00C781] border-[#00C781]/30 bg-[#00C781]/10",
  amber: "text-[#FFB020] border-[#FFB020]/30 bg-[#FFB020]/10",
  red: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10",
  blue: "text-[#3395FF] border-[#007AFF]/30 bg-[#007AFF]/10",
  gray: "text-zinc-400 border-zinc-700 bg-zinc-800/40",
};

const MAP = {
  eligible: "green", valid: "green", approved: "green", active: "green", sent: "green", connected: "green", held: "green",
  completed: "blue", booked: "blue", engaged: "blue", meeting_booked: "blue", qualified: "blue", opportunity: "blue", won: "green",
  simulated: "amber", unknown: "amber", risky: "amber", unverified: "amber", paused: "amber", draft: "gray", pending_approval: "amber",
  queued: "gray", nurture: "amber", open: "amber", medium: "amber", running: "blue", supervised: "amber", autonomous: "green",
  withdrawn: "red", bounced: "red", suppressed: "red", blocked: "red", stopped: "red", critical: "red", high: "red", failed: "red",
  rejected: "red", cancelled: "red", no_show: "red", not_connected: "gray", expired: "red", low: "gray", lost: "red",
};

export const Badge = ({ v, tone, className, ...p }) => (
  <span className={cn("inline-flex items-center rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide whitespace-nowrap", TONES[tone || MAP[v] || "gray"], className)} {...p}>
    {String(v ?? "—").replace(/_/g, " ")}
  </span>
);

export const Panel = ({ title, action, children, className, ...p }) => (
  <section className={cn("bg-[#121212] border border-border rounded-md", className)} {...p}>
    {title && (
      <div className="flex items-center justify-between px-5 py-3 border-b border-border">
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-300">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Stat = ({ label, value, sub, tone, testid }) => (
  <div className="bg-[#121212] border border-border p-5 rounded-md hover:border-zinc-600 transition-colors" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
    <div className={cn("text-3xl font-extrabold font-display tnum mt-2", tone)}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export const Empty = ({ children }) => <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-md">{children}</div>;

export const Md = ({ text }) => {
  const html = (text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>").replace(/^## (.*)$/gm, "<h2>$1</h2>").replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^\s*[-*] (.*)$/gm, "<li>$1</li>").replace(/(<li>.*<\/li>\n?)+/g, (m) => `<ul>${m}</ul>`)
    .replace(/\n{2,}/g, "<br/>");
  return <div className="md text-sm text-zinc-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
};

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-medium text-zinc-400">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-zinc-500">{hint}</span>}
  </label>
);
